import React, { type ReactNode } from 'react';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbDropdown,
    BreadcrumbDropdownItem,
} from './index';

export interface BreadcrumbItem {
    label: string;
    href?: string;
    isCurrentPage?: boolean;
}

export interface BreadcrumbBuilderProps {
    items: BreadcrumbItem[];
    maxItems?: number;
    showDropdown?: boolean;
    className?: string;
    separator?: ReactNode;
    renderItem?: (item: BreadcrumbItem, index: number) => ReactNode;
}

/**
 * A utility component that builds breadcrumbs from an array of items
 * Automatically handles truncation with dropdown for long paths
 */
export const BreadcrumbBuilder = ({
    items,
    maxItems = 5,
    showDropdown = true,
    className,
    separator,
    renderItem,
}: BreadcrumbBuilderProps) => {
    if (!items.length) {
        return null;
    }

    const shouldTruncate = items.length > maxItems && showDropdown;
    const visibleItems = shouldTruncate ? [...items.slice(0, 1), ...items.slice(-(maxItems - 1))] : items;

    const hiddenItems = shouldTruncate ? items.slice(1, Math.max(1, -maxItems + 2)) : [];

    const renderBreadcrumbItem = (item: BreadcrumbItem, index: number) => {
        if (renderItem) {
            return renderItem(item, index);
        }

        if (item.isCurrentPage) {
            return (
                <BreadcrumbItem key={`${item.label}-${index}`}>
                    <BreadcrumbPage>{item.label}</BreadcrumbPage>
                </BreadcrumbItem>
            );
        }

        const isFirstItem = index === 0;
        const linkClassName = isFirstItem ? 'text-primary-600 hover:text-primary-800' : '';

        return (
            <BreadcrumbItem key={`${item.label}-${index}`}>
                <BreadcrumbLink to={item.href || '#'} className={linkClassName}>
                    {item.label}
                </BreadcrumbLink>
            </BreadcrumbItem>
        );
    };

    return (
        <Breadcrumb className={className}>
            <BreadcrumbList>
                {visibleItems.map((item, index) => {
                    const isFirst = index === 0;
                    const isTruncated = shouldTruncate && index === 1;

                    return (
                        <React.Fragment key={`${item.label}-${index}`}>
                            {!isFirst && <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>}

                            {isTruncated && hiddenItems.length > 0 ? (
                                <BreadcrumbItem>
                                    <BreadcrumbDropdown>
                                        {hiddenItems.map((hiddenItem, hiddenIndex) => (
                                            <BreadcrumbDropdownItem
                                                key={`hidden-${hiddenItem.label}-${hiddenIndex}`}
                                                to={hiddenItem.href || '#'}
                                            >
                                                {hiddenItem.label}
                                            </BreadcrumbDropdownItem>
                                        ))}
                                    </BreadcrumbDropdown>
                                </BreadcrumbItem>
                            ) : (
                                renderBreadcrumbItem(item, index)
                            )}
                        </React.Fragment>
                    );
                })}
            </BreadcrumbList>
        </Breadcrumb>
    );
};

export default BreadcrumbBuilder;
